/**
 * Calls for the Player Profile (routes/profile.ts). Live data and every action go through the site's
 * own API, which checks permissions and then asks the game over the bridge; the browser never talks
 * to the game.
 *
 * Reads and actions that reach the game answer {ok:true, data} or {ok:false, error, message}. The
 * message is plain text written for staff and is always shown.
 */

import { NotAuthorizedError } from "./api";

export type BridgeAnswer<T> =
  | { ok: true; server?: string | null; data: T }
  | { ok: false; error: string; message?: string | null };

/** Item JSON, the same shape the dupe item card renders, plus shulker/bundle contents. */
export type Item = Record<string, unknown> & { material?: string; amount?: number; contents?: Array<Item | null> };

export interface Grid {
  hotbar?: Array<Item | null>;
  main?: Array<Item | null>;
  armor?: { helmet?: Item | null; chestplate?: Item | null; leggings?: Item | null; boots?: Item | null };
  offhand?: Item | null;
}

export interface ProfileData {
  uuid?: string;
  name?: string;
  firstSeen?: number;
  lastSeen?: number;
  online?: boolean;
  server?: { id?: string; label?: string } | null;
  proxy?: string | null;
  location?: { world?: string; x?: number; y?: number; z?: number } | null;
  ping?: number;
  gameMode?: string;
  health?: number;
  maxHealth?: number;
  food?: number;
  level?: number;
  xp?: number;
  lastServer?: { id?: string; label?: string } | null;
  lastLogout?: { serverId?: string; world?: string; x?: number; y?: number; z?: number; at?: number } | null;
  rank?: { primary?: string; groups?: string[] };
  staffMode?: boolean;
  banned?: Record<string, unknown> | null;
  muted?: Record<string, unknown> | null;
  balances?: Record<string, number>;
  stats?: Record<string, number>;
  crateKeys?: Record<string, number>;
  settings?: Array<{ id?: string; name?: string; value?: boolean | string; default?: boolean }>;
  homes?: Array<{ slot?: number; name?: string; serverId?: string; world?: string; x?: number; y?: number; z?: number }>;
  team?: { name?: string; role?: string } | null;
  ipLocked?: boolean;
}

export interface InventoryData {
  source?: "live" | "saved";
  savedAt?: number;
  inventory?: Grid;
  enderChest?: Array<Item | null>;
}

export interface SnapshotRow {
  id: number;
  timestamp: number;
  reason: string | null;
  server: string | null;
  gamemode: string | null;
  health: number | null;
  food: number | null;
  level: number | null;
  money: number | null;
  prisms: number | null;
}

export interface SnapshotData {
  meta?: Record<string, unknown>;
  inventory?: Grid;
  enderChest?: Array<Item | null>;
  effects?: Array<{ type?: string; amplifier?: number; secondsLeft?: number }>;
  homes?: Array<Record<string, unknown>>;
  extras?: Record<string, unknown>;
}

export interface TimelineEntry {
  kind: "ban" | "mute" | "kick";
  id: string | null;
  reason: string;
  issuer: string;
  issuedAt: number;
  expiresAt: number | null;
  state: string;
  ipBan: boolean;
  protected: boolean;
}

export interface Reason {
  key: string;
  type: "ban" | "mute";
  baseDuration: string;
  message: string;
  wipe: boolean;
  ipBan: boolean;
}

export interface PunishPreview {
  type: "ban" | "mute";
  offence: number;
  duration: string;
  message: string;
  wipe: boolean;
  ipBan: boolean;
  alreadyActive: boolean;
}

export interface ActionResult {
  done?: boolean;
  summary?: string;
  appliedNow?: boolean;
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin", signal });
  if (response.status === 403) throw new NotAuthorizedError();
  const json = (await response.json().catch(() => ({}))) as T & { error?: string; message?: string };
  if (!response.ok) throw new Error(json.message ?? json.error ?? `HTTP ${response.status}`);
  return json;
}

export const profileApi = {
  search: (q: string, signal?: AbortSignal) =>
    getJson<{ players: Array<{ uuid: string; name: string; online: boolean }> }>(
      `/api/profile/search?q=${encodeURIComponent(q)}`,
      signal,
    ),
  resolve: (id: string) => getJson<{ uuid: string; name: string | null }>(`/api/profile/resolve/${encodeURIComponent(id)}`),
  profile: (uuid: string) => getJson<BridgeAnswer<ProfileData> & { name: string | null }>(`/api/profile/${uuid}/profile`),
  inventory: (uuid: string) => getJson<BridgeAnswer<InventoryData>>(`/api/profile/${uuid}/inventory`),
  snapshots: (uuid: string, query: string, cursor: string | null) =>
    getJson<{ snapshots: SnapshotRow[]; servers: string[]; nextCursor: string | null }>(
      `/api/profile/${uuid}/snapshots?${query}${cursor ? `${query ? "&" : ""}cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),
  snapshot: (uuid: string, timestamp: number) => getJson<BridgeAnswer<SnapshotData>>(`/api/profile/${uuid}/snapshots/${timestamp}`),
  punishments: (uuid: string) => getJson<{ name: string | null; timeline: TimelineEntry[] }>(`/api/profile/${uuid}/punishments`),
  reasons: () => getJson<BridgeAnswer<Reason[]>>("/api/profile/reasons"),
  preview: (uuid: string, reason: string) =>
    getJson<BridgeAnswer<PunishPreview>>(`/api/profile/${uuid}/punish-preview?reason=${encodeURIComponent(reason)}`),
  actions: (uuid: string) =>
    getJson<{ actions: Array<{ id: number; at: number; by: string; detail: string }> }>(`/api/profile/${uuid}/actions`),

  /** Sends one action. Never retried — a timeout may still have happened in game. */
  async act(uuid: string, op: string, args: Record<string, unknown>, csrfToken: string): Promise<BridgeAnswer<ActionResult>> {
    const response = await fetch(`/api/profile/${uuid}/action`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify({ op, args }),
    });
    if (response.status === 403) return { ok: false, error: "no_permission", message: "You are not allowed to do that." };
    if (response.status === 429) return { ok: false, error: "busy", message: "Too many actions — wait a minute and try again." };
    const json = (await response.json().catch(() => ({}))) as BridgeAnswer<ActionResult>;
    return json;
  },
};

/** Who may use the profile while it is in preview. Mirrors PROFILE_PREVIEW_USERS on the server. */
export const PROFILE_PREVIEW_USERS: readonly string[] = ["admin"];

/** User-facing text for a failed bridge read. */
export function readError(answer: { error: string; message?: string | null }): string {
  if (answer.error === "timeout") return "The game servers did not answer — try again.";
  if (answer.error === "bridge_off") return answer.message ?? "The game bridge is not configured.";
  return answer.message ?? `Error: ${answer.error}`;
}
